import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { X } from 'lucide-react';
import { RunOutcome, RunTrigger, type TelemetryOutcome } from '@signalvote/shared';
import { Button } from '@/components/ui';
import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table';
import { useAssistantTelemetryRuns } from '@/hooks/queries';
import { useCampaignStore } from '@/stores';
import { FilterSelect } from './FilterSelect';
import { formatCost, formatCount, formatInstant, formatMs } from './telemetryDisplay';

const TRIGGERS = Object.values(RunTrigger).map((t) => ({ value: t, label: t }));
const OUTCOMES: { value: TelemetryOutcome; label: string }[] = [
  ...Object.values(RunOutcome).map((o) => ({ value: o, label: o })),
  { value: 'Open', label: 'Open' },
];

interface TelemetryRunListProps {
  campaignId: string | undefined;
  from: string | undefined;
  to: string | undefined;
}

export function TelemetryRunList({ campaignId, from, to }: TelemetryRunListProps) {
  const [user, setUser] = useState<{ id: string; label: string }>();
  const [trigger, setTrigger] = useState<RunTrigger>();
  const [outcome, setOutcome] = useState<TelemetryOutcome>();
  const [page, setPage] = useState(1);
  const timeZones = new Map(useCampaignStore((s) => s.campaigns).map((c) => [c.id, c.timeZoneId]));
  const runs = useAssistantTelemetryRuns({ campaignId, userId: user?.id, trigger, outcome, from, to, page });

  const filtered = <T,>(set: (value: T) => void) => (value: T) => {
    set(value);
    setPage(1);
  };

  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold text-foreground">Runs</h2>
      <div className="flex flex-wrap items-end gap-3">
        <FilterSelect label="Trigger" value={trigger} onChange={filtered(setTrigger)} options={TRIGGERS} />
        <FilterSelect label="Outcome" value={outcome} onChange={filtered(setOutcome)} options={OUTCOMES} />
        {user && (
          <Badge variant="secondary" className="h-9 gap-1">
            User: {user.label}
            <button type="button" aria-label="Clear user filter" onClick={() => filtered(setUser)(undefined)}>
              <X className="h-3 w-3" />
            </button>
          </Badge>
        )}
      </div>

      {runs.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading runs…</p>
      ) : runs.isError || !runs.data ? (
        <p className="text-sm text-destructive">Could not load runs.</p>
      ) : runs.data.items.length === 0 ? (
        <p className="text-sm text-muted-foreground">No runs match.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Started</TableHead>
              <TableHead>Campaign</TableHead>
              <TableHead>User</TableHead>
              <TableHead>Trigger</TableHead>
              <TableHead>Outcome</TableHead>
              <TableHead>Model</TableHead>
              <TableHead className="text-right">First text server / client</TableHead>
              <TableHead className="text-right">Client done</TableHead>
              <TableHead className="text-right">Tools</TableHead>
              <TableHead className="text-right">Tokens in / out</TableHead>
              <TableHead className="text-right">Cost (est.)</TableHead>
              <TableHead className="text-right">Up / down</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {runs.data.items.map((run) => {
              const userLabel = run.userName ?? run.userEmail ?? run.userId;
              return (
                <TableRow key={run.id}>
                  <TableCell className="whitespace-nowrap">
                    <Link
                      to="/admin/assistant-telemetry/runs/$runId"
                      params={{ runId: run.id }}
                      className="text-primary hover:underline"
                    >
                      {formatInstant(run.startedAt, timeZones.get(run.campaignId))}
                    </Link>
                  </TableCell>
                  <TableCell>{run.campaignName}</TableCell>
                  <TableCell>
                    <button
                      type="button"
                      className="hover:underline"
                      title="Show only this user's runs"
                      onClick={() => filtered(setUser)({ id: run.userId, label: userLabel })}
                    >
                      {userLabel}
                    </button>
                  </TableCell>
                  <TableCell className="text-xs">{run.trigger}</TableCell>
                  <TableCell>
                    <Badge variant={run.outcome === RunOutcome.Completed ? 'secondary' : run.outcome ? 'destructive' : 'outline'}>
                      {run.outcome ?? 'Open'}
                    </Badge>
                    {run.errorClass && <div className="mt-1 text-xs text-muted-foreground">{run.errorClass}</div>}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{run.model ?? '—'}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatMs(run.firstTextMs)} / {formatMs(run.clientFirstTextMs)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{formatMs(run.clientDoneMs)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatCount(run.toolCalls)}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatCount(run.inputTokens)} / {formatCount(run.outputTokens)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{formatCost(run.estimatedCostUsd)}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {run.thumbsUp} / {run.thumbsDown}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
          Previous
        </Button>
        <span className="text-sm text-muted-foreground tabular-nums">Page {page}</span>
        <Button variant="outline" size="sm" disabled={!runs.data?.hasMore} onClick={() => setPage((p) => p + 1)}>
          Next
        </Button>
      </div>
    </section>
  );
}
